import React, { useState, useEffect, useMemo } from 'react';
import LodgingFilterBar from '../../components/lodging/LodgingFilterBar';
import LodgingMapView from '../../components/lodging/LodgingMapView';
import LodgingList from '../../components/lodging/LodgingList';
import LodgingLoadMoreButton from '../../components/lodging/LodgingLoadMoreButton';
import { fetchAllLodgings } from '../../lib/api';
import { applyFilters } from '../../lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { readSessionJson, writeSessionJson } from '../../utils/storage';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const PAGE_SIZE = 24;
const STORAGE_KEY = 'alakai_lodging_index_state_v2';

const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const toString = (value) => (typeof value === 'string' ? value : '');

const normalizeFilters = (raw) => ({
  region: toArray(raw?.region),
  priceRange: toArray(raw?.priceRange),
  searchText: toString(raw?.searchText),
});

const LodgingIndex = () => {
  const location = useLocation();
  const { island, islandConfig } = getRuntimeSiteContext();
  const storageKey = useMemo(() => `${STORAGE_KEY}_${island}`, [island]);
  const stored = useMemo(() => readSessionJson(storageKey), [storageKey]);
  const [all, setAll] = useState([]);
  const defaultFilters = useMemo(
    () => ({
      region: [], priceRange: [], searchText: ''
    }),
    []
  );
  const [filters, setFilters] = useState(() => {
    const normalized = normalizeFilters(stored?.filters);
    return { ...defaultFilters, ...normalized };
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [mapBounds, setMapBounds] = useState(null);
  const mapViewRef = React.useRef(stored?.mapView ?? null);

  // Initialize filters from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const regionParam = searchParams.get('region');
    
    if (regionParam) {
      setFilters(prev => ({
        ...prev,
        region: [regionParam]
      }));
    }
  }, [location.search]);

  // Load all lodging once, then shuffle to get a random selection
  useEffect(() => {
    fetchAllLodgings().then(data => {
      const shuffled = data.sort(() => 0.5 - Math.random());
      setAll(shuffled);
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    writeSessionJson(storageKey, {
      filters,
      mapView: mapViewRef.current,
      storedAt: Date.now(),
    });
  }, [filters, storageKey]);

  const filtered = useMemo(
    () => applyFilters(all, filters),
    [all, filters]
  );

  const inViewport = useMemo(() => {
    if (!mapBounds) return filtered;
    return filtered.filter(l => {
      const { lat, lng } = l.location;
      return mapBounds.contains({ lat, lng });
    });
  }, [filtered, mapBounds]);

  const displayed = useMemo(
    () => inViewport.slice(0, currentPage * PAGE_SIZE),
    [inViewport, currentPage]
  );

  return (
    <div className="lodgings-index-page pt-0 space-y-6">
      <Helmet>
        <title>{`${islandConfig.title} Lodging | Alaka'i Aloha`}</title>
        <meta
          name="description"
          content={`Find ${islandConfig.title} lodging options with map view, filters, and local recommendations for where to stay.`}
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl('/lodging')} />
      </Helmet>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-4xl font-bold">Lodgings</h1>
        <Link
          to="/lodging/list"
          className="btn-secondary-compact"
          title="View a complete list of all lodgings"
        >
          View as list
        </Link>
      </div>

      <LodgingFilterBar filters={filters} onChange={setFilters} />

      <div className="mt-6">
        <LodgingMapView
          lodgings={displayed}
          onBoundsChanged={setMapBounds}
          initialCenter={mapViewRef.current?.center}
          initialZoom={mapViewRef.current?.zoom}
          onViewChanged={(nextView) => {
            mapViewRef.current = nextView;
            writeSessionJson(storageKey, {
              filters,
              mapView: nextView,
              storedAt: Date.now(),
            });
          }}
        />
      </div>

      <div className="mt-6">
        <LodgingList lodgings={displayed} />
      </div>

      {displayed.length < inViewport.length && (
        <LodgingLoadMoreButton 
          onClick={() => setCurrentPage(p => p + 1)} />
      )}
    </div>
  );
};

export default LodgingIndex;
